import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useLocaleKeys } from '~/i18n';
import { PreapprovalPeriodType } from '~/models/service-billing/model';
import pluralize from '~/utils/pluralize';
import { PreapprovalSettingsRow } from '../../PreapprovalFields/PreapprovalSettingsRow';
import styles from '../ServiceBuilder.module.scss';
import { getPreapprovedSuffix } from '../ServiceBuilder.utils';
export default function PreapprovedSection() {
    const { t: unitsText } = useLocaleKeys('billing.preapproval.unitFallback');
    const { t: validationTexts } = useLocaleKeys('billing.preapproval.validation');
    const { watch, setValue, getValues, control } = useFormContext();
    const [isPreapprovedEnabled, setIsPreapprovedEnabled] = useState(()=>{
        const cap = getValues('unitCap');
        return !!(cap && cap.trim() !== '' && +cap > 0);
    });
    const [unitCap, recurrencePeriod, pricing, unitName, preapprovalPeriod] = watch([
        'unitCap',
        'recurrencePeriod',
        'pricing',
        'unitName',
        'preapprovalPeriod'
    ]);
    const isOnDemand = recurrencePeriod === 'onDemand';
    const suffixAmount = useMemo(()=>{
        const periodSuffix = getPreapprovedSuffix(recurrencePeriod);
        if (periodSuffix) return periodSuffix;
        return pricing === 'unit' ? pluralize(unitName) || unitsText.units() : unitsText.hours();
    }, [
        recurrencePeriod,
        pricing,
        unitName,
        unitsText
    ]);
    const validate = (value)=>{
        if (!isPreapprovedEnabled) return true;
        if (!value || +value <= 0) return validationTexts.setCapPositive();
        return true;
    };
    const handleCheckboxChange = (checked)=>{
        setIsPreapprovedEnabled(checked);
        if (!checked) {
            setValue('unitCap', '', {
                shouldDirty: true
            });
        } else {
            if (isOnDemand) {
                const currentPeriod = getValues('preapprovalPeriod');
                if (!currentPeriod || currentPeriod === PreapprovalPeriodType.None) {
                    setValue('preapprovalPeriod', PreapprovalPeriodType.Monthly, {
                        shouldDirty: true
                    });
                }
            }
            if (unitCap === '0') {
                setValue('unitCap', '', {
                    shouldDirty: true
                });
            }
        }
    };
    const handlePeriodChange = (event)=>{
        setValue('preapprovalPeriod', event.target.value, {
            shouldDirty: true
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        className: styles.preapprovedSection,
        children: /*#__PURE__*/ _jsx(Controller, {
            control: control,
            name: "unitCap",
            rules: {
                validate
            },
            render: (param)=>{
                let { field, fieldState } = param;
                return /*#__PURE__*/ _jsx(PreapprovalSettingsRow, {
                    hideFieldsWhenUnchecked: true,
                    fullyStretchedAmount: true,
                    checked: isPreapprovedEnabled,
                    onCheckedChange: handleCheckboxChange,
                    amount: field.value || '',
                    onAmountChange: (value)=>field.onChange(value),
                    amountError: !!fieldState.error,
                    amountSuffix: suffixAmount && /*#__PURE__*/ _jsx("span", {
                        "data-testid": "sb-cap-suffix",
                        children: suffixAmount
                    }),
                    showPeriodDropdown: isOnDemand,
                    periodValue: preapprovalPeriod,
                    onPeriodChange: handlePeriodChange,
                    className: styles.equalWidthChildren
                });
            }
        })
    });
}
